import { useCallback } from 'react';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import type { GetConfluenceContentResponse, GetConfluenceContentVariables } from './types';

const GetConfluenceContentQuery = `
query GetConfluenceContent($cloudId: ID!, $contentId: ID!, $includeReactions: Boolean!) {
  content(cloudId: $cloudId, id: $contentId) {
    nodes {
      id
      title
      type
      subType
      space {
        id
        key
      }
      links {
        webui
        base
      }
      version {
        friendlyWhen
      }
      excerpt(length: 372)
    }
  }
  contentContributors(id: $contentId, cloudId: $cloudId) {
    nodes {
      type
      displayName
      ... on KnownUser {
        accountId
      }
      profilePicture {
        path
      }
    }
  }
  confluence_contentReactionsSummary(cloudId: $cloudId, contentId: $contentId, contentType: PAGE) @include(if: $includeReactions) {
    contentReactionsSummary: reactionsSummary {
      reactionsCount
      ari
      containerAri
      reactionsSummaryForEmoji {
        emojiId
        count
        reacted
        id
      }
    }
  }
}
`;

export const useConfluenceContent = (
	contentId: string,
	cloudId: string,
	includeReactions: boolean,
): {
	getConfluenceContent: () => Promise<GetConfluenceContentResponse | undefined>;
} => {
	const getConfluenceContent = useCallback(async (): Promise<
		GetConfluenceContentResponse | undefined
	> => {
		try {
			const response = await performGqlFetch<
				GetConfluenceContentResponse,
				GetConfluenceContentVariables
			>({
				gqlQuery: GetConfluenceContentQuery,
				variables: {
					contentId,
					cloudId,
					includeReactions,
				},
			});

			return response?.data;
		} catch (error) {
			logException({
				exception: error,
				name: 'GetConfluenceContentQueryError',
				tags: { contentId },
			});
			return undefined;
		}
	}, [contentId, cloudId, includeReactions]);

	return {
		getConfluenceContent,
	};
};
